import { CBUserProfile, SocketClient } from "cbcore-ts"
import {
    FIRST,
    FIRST_OR_NIL,
    nil,
    NO,
    UIButton,
    UIColor,
    UIRoute,
    UITextView,
    UIView,
    UIViewController,
    wrapInNil,
    YES
} from "uicore-ts"
import { CBButton } from "./Custom components/CB/CBButton"
import { CBDataView } from "./Custom components/CB/CBDataView"
import { CBDialogViewShower } from "./Custom components/CB/CBDialogViewShower"
import { ClientInstanceConnection, ClientInstanceConnectionEvent } from "./SocketClientFunctions"


// A connection as the dialog lists it: the user profile reduced to the text shown for it.
type InstanceConnectionRow = Omit<ClientInstanceConnection, "userProfile"> & { userProfile: string }


type UsageUserProfile = NonNullable<Awaited<ReturnType<typeof SocketClient.RetrieveUserProfiles>>["result"]>[number]

// One time step of the usage table, with the identifiers behind each count for the detail dialogs.
interface UsageDataRow {
    date: string
    minDate: Date
    maxDate: Date
    visitors: number
    visitorIdentifiers: string[]
    normalUsers: number
    normalUserProfiles: UsageUserProfile[]
    notRegisteredUsers: number
    firstTimeUsers: number
    firstTimeUserIdentifiers: string[]
    administrators: number
    administratorsUserProfiles: UsageUserProfile[]
}

// The parts of the chart library's chart that this page updates.
interface UsageChart {
    data: { labels: string[], datasets: { data: number[] }[] }
    update(): void
}


export class InternalUsageDataViewController extends UIViewController {
    
    
    static override readonly routeComponentName = "internal_usage_data"
    static override readonly ParameterIdentifierName = {}
    titleLabel: UITextView
    loadButton: UIButton
    chartView: UIView
    dateView: UIView
    minDateInput: HTMLInputElement
    maxDateInput: HTMLInputElement
    dateStepInput: HTMLInputElement
    chart: UsageChart
    downloadButton: CBButton
    primaryData: UsageDataRow[] = []
    private tableView: CBDataView<UsageDataRow>
    
    constructor(view: UIView) {
        
        // Calling super
        super(view)
        
        // Code for further setup if necessary
        
        this.view.backgroundColor = UIColor.whiteColor
        
        this.titleLabel = new UITextView(this.view.elementID + "TitleLabel", UITextView.type.header2)
        this.titleLabel.text = "Internal usage data"
        this.view.addSubview(this.titleLabel)
        
        
        this.chartView = new UIView("UsageDataChart", nil, "canvas")
        this.view.addSubview(this.chartView)
        
        
        this.loadButton = new CBButton(this.view.elementID + "LoadButton")
        this.loadButton.titleLabel.text = "Load"
        this.view.addSubview(this.loadButton)
        
        this.downloadButton = new CBButton(this.view.elementID + "DownloadButton")
        this.downloadButton.titleLabel.text = "Download"
        this.view.addSubview(this.downloadButton)
        
        
        this.dateView = new UIView(this.view.elementID + "DataView")
        this.dateView.innerHTML = "From <input type=\"datetime-local\" id=\"MinDateInput\" /> To " +
            "<input type=\"datetime-local\" id=\"MaxDateInput\" /> With a step of " +
            "<input type=\"number\" id=\"DateStepInput\" /> hours"
        
        this.view.addSubview(this.dateView)
        
        
        this.minDateInput = this.view.viewHTMLElement.querySelector<HTMLInputElement>("#MinDateInput")!
        this.maxDateInput = this.view.viewHTMLElement.querySelector<HTMLInputElement>("#MaxDateInput")!
        this.dateStepInput = this.view.viewHTMLElement.querySelector<HTMLInputElement>("#DateStepInput")!
        
        const offsetInMinutes = new Date().getTimezoneOffset()
        
        this.minDateInput.value = new Date(Date.now() - 1000 * 60 * 60 * 24 * 7 - 1000 * 60 *
            offsetInMinutes).toISOString().slice(0, 16)
        this.maxDateInput.value = new Date(Date.now() - 1000 * 60 * offsetInMinutes).toISOString().slice(0, 16)
        this.dateStepInput.value = "24"
        
        
        // this.textArea = new UITextArea(this.view.elementID + "TextArea")
        // this.view.addSubview(this.textArea)
        
        this.tableView = new CBDataView<UsageDataRow>(this.view.elementID + "TableView")
        
        this.tableView.descriptors = [
            
            { keyPath: "date", title: "Date", allowSorting: YES },
            {
                keyPath: "visitors",
                title: "Visitors",
                defaultTitle: "0",
                allowSorting: YES,
                buttonWasPressed: async (
                    cellView,
                    dataItem
                ) => {
                    
                    CBDialogViewShower.showActionIndicatorDialog("Loading data.")
                    
                    InternalUsageDataViewController.showConnectionsDialogView(
                        await this.retrieveConnectionsForInstanceIdentifiers(
                            dataItem.visitorIdentifiers,
                            dataItem.minDate,
                            dataItem.maxDate
                        )
                    )
                    
                    CBDialogViewShower.hideActionIndicatorDialog()
                    
                }
            },
            // {
            //     keyPath: "merchants",
            //     title: "Merchants",
            //     defaultTitle: "0",
            //     allowSorting: YES,
            //     buttonWasPressed: async (
            //         cellView,
            //         dataItem
            //     ) => {
            //        
            //         CBDialogViewShower.showActionIndicatorDialog("Loading data.")
            //        
            //         InternalUsageDataViewController.showConnectionsDialogView(
            //             await this.retrieveConnectionsForUserProfiles(
            //                 dataItem.merchantUserProfiles,
            //                 dataItem.minDate,
            //                 dataItem.maxDate
            //             )
            //         )
            //        
            //         CBDialogViewShower.hideActionIndicatorDialog()
            //        
            //     }
            // },
            {
                keyPath: "normalUsers",
                title: "Normal users",
                defaultTitle: "0",
                allowSorting: YES,
                buttonWasPressed: async (
                    cellView,
                    dataItem
                ) => {
                    
                    CBDialogViewShower.showActionIndicatorDialog("Loading data.")
                    
                    InternalUsageDataViewController.showConnectionsDialogView(
                        await this.retrieveConnectionsForUserProfiles(
                            dataItem.normalUserProfiles,
                            dataItem.minDate,
                            dataItem.maxDate
                        )
                    )
                    
                    CBDialogViewShower.hideActionIndicatorDialog()
                    
                }
            },
            { keyPath: "notRegisteredUsers", title: "Not registered users", defaultTitle: "0", allowSorting: YES },
            {
                keyPath: "firstTimeUsers",
                title: "First time users",
                defaultTitle: "0",
                allowSorting: YES,
                buttonWasPressed: async (
                    cellView,
                    dataItem
                ) => {
                    
                    CBDialogViewShower.showActionIndicatorDialog("Loading data.")
                    
                    InternalUsageDataViewController.showConnectionsDialogView(
                        await this.retrieveConnectionsForInstanceIdentifiers(
                            dataItem.firstTimeUserIdentifiers,
                            dataItem.minDate,
                            dataItem.maxDate
                        )
                    )
                    
                    CBDialogViewShower.hideActionIndicatorDialog()
                    
                }
            },
            {
                keyPath: "administrators",
                title: "Administrators",
                defaultTitle: "0",
                allowSorting: YES,
                buttonWasPressed: async (
                    cellView,
                    dataItem
                ) => {
                    
                    CBDialogViewShower.showActionIndicatorDialog("Loading data.")
                    
                    InternalUsageDataViewController.showConnectionsDialogView(
                        await this.retrieveConnectionsForUserProfiles(
                            dataItem.administratorsUserProfiles,
                            dataItem.minDate,
                            dataItem.maxDate
                        )
                    )
                    
                    CBDialogViewShower.hideActionIndicatorDialog()
                    
                }
            }//,
            // { keyPath: "inquiries", title: "Inquiries", defaultTitle: "0", allowSorting: YES },
            // {
            //    
            //     keyPath: "acceptedOffers",
            //     title: "Accepted inquiry offers",
            //     defaultTitle: "0",
            //     allowSorting: YES,
            //     buttonWasPressed: (cellView, dataItem) => InternalUsageDataViewController.showConnectionsDialogView(
            //         dataItem.acceptedOfferConnections
            //     )
            //    
            // }
        
        ]
        
        
        this.view.addSubview(this.tableView)
        
        
        // @ts-ignore
        const ctx = this.chartView.viewHTMLElement.getContext("2d")
        
        // @ts-ignore
        this.chart = new Chart(ctx, {
            // The type of chart we want to create
            type: "line",
            
            // The data for our dataset
            data: {
                //labels: ['January', 'February', 'March', 'April', 'May', 'June'],
                datasets: [
                    
                    {
                        label: "Visitors",
                        fill: false,
                        lineTension: 0,
                        backgroundColor: "rgb(255, 99, 132)",
                        borderColor: "rgb(255, 99, 132)" //,
                        //data: [0, 10, 5, 2, 20, 45]
                    },
                    // {
                    //     label: "Merchants",
                    //     fill: false,
                    //     lineTension: 0,
                    //     backgroundColor: CBColor.primaryTintColor.stringValue,
                    //     borderColor: CBColor.primaryTintColor.stringValue //,
                    //     //data: [0, 10, 5, 2, 20, 45]
                    // },
                    {
                        label: "Normal users",
                        fill: false,
                        lineTension: 0,
                        backgroundColor: UIColor.greenColor.stringValue,
                        borderColor: UIColor.greenColor.stringValue //,
                        //data: [0, 10, 5, 2, 20, 45]
                    },
                    {
                        label: "Not registered users",
                        fill: false,
                        lineTension: 0,
                        backgroundColor: UIColor.greyColor.stringValue,
                        borderColor: UIColor.greyColor.stringValue //,
                        //data: [0, 10, 5, 2, 20, 45]
                    },
                    {
                        label: "First time users",
                        fill: false,
                        lineTension: 0,
                        backgroundColor: UIColor.colorWithRGBA(210, 210, 210).stringValue,
                        borderColor: UIColor.colorWithRGBA(210, 210, 210).stringValue //,
                        //data: [0, 10, 5, 2, 20, 45]
                    },
                    {
                        label: "Administrators",
                        fill: false,
                        lineTension: 0,
                        backgroundColor: UIColor.colorWithRGBA(210, 121, 210).stringValue,
                        borderColor: UIColor.colorWithRGBA(210, 121, 210).stringValue //,
                        //data: [0, 10, 5, 2, 20, 45]
                    } //,
                    // {
                    //     label: "Inquiries",
                    //     fill: false,
                    //     lineTension: 0,
                    //     backgroundColor: UIColor.colorWithRGBA(150, 251, 150).stringValue,
                    //     borderColor: UIColor.colorWithRGBA(150, 251, 150).stringValue //,
                    //     //data: [0, 10, 5, 2, 20, 45]
                    // },
                    // {
                    //     label: "Inquiry offers",
                    //     fill: false,
                    //     lineTension: 0,
                    //     backgroundColor: UIColor.colorWithRGBA(75, 251, 75).stringValue,
                    //     borderColor: UIColor.colorWithRGBA(75, 251, 75).stringValue //,
                    //     //data: [0, 10, 5, 2, 20, 45]
                    // },
                    // {
                    //     label: "Accepted inquiry offers",
                    //     fill: false,
                    //     lineTension: 0,
                    //     backgroundColor: UIColor.colorWithRGBA(75, 151, 175).stringValue,
                    //     borderColor: UIColor.colorWithRGBA(75, 151, 175).stringValue //,
                    //     //data: [0, 10, 5, 2, 20, 45]
                    // }
                
                ]
            },
            
            // Configuration options go here
            options: {
                fill: false,
                lineTension: 0
            }
            
        })
        
        
        this.loadData()
        
        
        this.loadButton.addTargetForControlEvent(
            UIView.controlEvent.PointerUpInside,
            function (this: InternalUsageDataViewController, sender: UIButton, event: Event) {
                
                this.loadData()
                
            }.bind(this)
        )
        
        this.downloadButton.addTargetForControlEvent(UIView.controlEvent.PointerUpInside, async (sender, event) => {
            
            await this.loadData()
            
            const minDate = new Date(this.minDateInput.value)
            const maxDate = new Date(this.maxDateInput.value)
            
            this.JSONToCSVConverter(this.primaryData, "Usage " + minDate.dateString + " - " + maxDate.dateString +
                " with a step of " + this.dateStepInput.value + " hours")
            
        })
        
        
    }
    
    private static showConnectionsDialogView(connections: ClientInstanceConnection[]) {
        
        const dataView = new CBDataView<InstanceConnectionRow>()
        
        dataView.intrinsicContentHeight = () => 551
        
        dataView.intrinsicContentWidth = () => 1000
        
        dataView.descriptors = [
            
            {
                keyPath: "userProfile",
                title: "User profile identifier",
                allowSorting: YES
            },
            { keyPath: "instanceIdentifierKey", title: "Client identifier", allowSorting: YES },
            {
                keyPath: "_id",
                title: "Connection identifier",
                allowSorting: YES,
                buttonWasPressed: async (cellView, dataItem) => {
                    
                    InternalUsageDataViewController.showConnectionEventsDialogView(
                        (await SocketClient.RetrieveInstanceConnectionEvents([dataItem._id])).result,
                        () => CBDialogViewShower.showDialog(dataView)
                    )
                    
                    dataView.dialogView?.dismiss()
                    
                }
            }
        
        ]
        
        dataView.data = connections.map((connection): InstanceConnectionRow => {
            
            return {
                ...connection,
                userProfile: connection.userProfile?.contactInformation?.email ??
                    connection.userProfile?._id ??
                    "Not registered user"
            }
            
        }).reverse()
        
        CBDialogViewShower.showDialog(dataView)
        
    }
    
    private static showConnectionEventsDialogView(
        connectionEvents: ClientInstanceConnectionEvent[],
        dismissCallback: Function
    ) {
        
        const dataView = new CBDataView<ClientInstanceConnectionEvent>()
        
        dataView.intrinsicContentHeight = () => 551
        
        dataView.intrinsicContentWidth = () => 1500
        
        dataView.descriptors = [
            
            { keyPath: "createdAt", title: "Date", allowSorting: YES },
            { keyPath: "socketMessageKey", title: "Socket message key", allowSorting: YES },
            { keyPath: "requestData", title: "Request data", allowSorting: YES },
            { keyPath: "errorData", title: "Error data", allowSorting: YES }
        
        ]
        
        dataView.data = connectionEvents
        
        CBDialogViewShower.showDialog(dataView, dismissCallback)
        
    }
    
    async loadData() {
        
        
        this.loadButton.enabled = NO
        this.downloadButton.enabled = NO
        
        CBDialogViewShower.showActionIndicatorDialog("Loading data")
        
        function getDates(startDate: Date, stopDate: Date, stepInHours: number) {
            
            const dateArray: Date[] = []
            
            const currentDate = startDate
            
            while (currentDate <= stopDate) {
                
                dateArray.push(new Date(currentDate))
                
                currentDate.setTime(currentDate.getTime() + 1000 * 60 * 60 * stepInHours)
                
                
            }
            
            return dateArray
            
        }
        
        
        const minDateValue = new Date(this.minDateInput.value)
        const maxDateValue = new Date(this.maxDateInput.value)
        
        const dates = getDates(minDateValue, maxDateValue, this.dateStepInput.value.numericalValue)
        
        
        // await RHCore.sharedInstance.inquiriesModel.updateFromServer()
        
        
        this.primaryData = []
        
        
        const visitorValues = [0].arrayByRepeating(dates.length - 1)
        
        const normalUserValues = [0].arrayByRepeating(dates.length - 1)
        
        const notRegisteredUserValues = [0].arrayByRepeating(dates.length - 1)
        
        const firstTimeUserValues = [0].arrayByRepeating(dates.length - 1)
        
        const administratorsValues = [0].arrayByRepeating(dates.length - 1)
        
        
        this.chart.data.labels = ["Loading"].arrayByRepeating(dates.length - 1)
        
        for (let i = 0; i < dates.length - 1; i++) {
            
            const minDate = dates[i]
            const maxDate = dates[i + 1]
            const primaryDataItem: Partial<UsageDataRow> = {}
            
            primaryDataItem.date = minDate.dateString + " - " + maxDate.dateString
            
            primaryDataItem.minDate = minDate
            
            primaryDataItem.maxDate = maxDate
            
            const instanceIdentifiers = (await SocketClient.RetrieveInstanceIdentifiers({
                minDate: minDate,
                maxDate: maxDate
            })).result ?? []
            
            
            visitorValues[i] = instanceIdentifiers.length
            primaryDataItem.visitors = visitorValues[i]
            primaryDataItem.visitorIdentifiers = instanceIdentifiers
            
            this.chart.data.datasets[0].data = visitorValues
            
            
            const userProfiles = (await SocketClient.RetrieveUserProfiles({
                minDate: minDate,
                maxDate: maxDate
            })).result ?? []
            
            
            normalUserValues[i] = userProfiles.length
            primaryDataItem.normalUsers = normalUserValues[i]
            primaryDataItem.normalUserProfiles = userProfiles.filter(
                // @ts-ignore
                (userProfile, index, array) => !userProfile.hasAValidSubscription
            )
            
            this.chart.data.datasets[1].data = normalUserValues
            
            
            notRegisteredUserValues[i] = instanceIdentifiers.length - userProfiles.length
            primaryDataItem.notRegisteredUsers = notRegisteredUserValues[i]
            
            this.chart.data.datasets[2].data = notRegisteredUserValues
            
            
            const firstTimeUserIdentifiers = (await SocketClient.RetrieveFirstTimeUserInstanceIdentifiers({
                minDate: minDate,
                maxDate: maxDate
            })).result ?? []
            
            
            firstTimeUserValues[i] = firstTimeUserIdentifiers.length
            primaryDataItem.firstTimeUsers = firstTimeUserValues[i]
            primaryDataItem.firstTimeUserIdentifiers = firstTimeUserIdentifiers
            
            this.chart.data.datasets[3].data = firstTimeUserValues
            
            
            const administratorsUserProfiles = userProfiles.filter(
                // @ts-ignore
                (userProfile, index, array) => userProfile.isAnAdministrator
            )
            
            administratorsValues[i] = administratorsUserProfiles.length
            primaryDataItem.administrators = administratorsValues[i]
            primaryDataItem.administratorsUserProfiles = administratorsUserProfiles
            
            this.chart.data.datasets[4].data = administratorsValues
            
            
            console.log(primaryDataItem)
            
            this.primaryData.push(primaryDataItem as UsageDataRow)
            
            this.chart.data.labels[i] = maxDate.dateString
            
            this.chart.update()
            
            
        }
        
        
        console.log(this.primaryData)
        
        this.tableView.data = this.primaryData.copy()
        
        
        CBDialogViewShower.hideActionIndicatorDialog()
        
        this.loadButton.enabled = YES
        this.downloadButton.enabled = YES
        
        
    }
    
    JSONToCSVConverter(JSONData: UsageDataRow[], ReportTitle: string, ShowLabel = YES) {
        
        //If JSONData is not an object then JSON.parse will parse the JSON string in an Object
        const arrData = JSONData  // typeof JSONData != 'object' ? JSON.parse(JSONData) : JSONData;
        
        var CSV = ""
        //Set Report title in first row or line
        
        CSV += ReportTitle + "\r\n\n"
        
        //This condition will generate the Label/Header
        if (ShowLabel) {
            var row = ""
            
            //This loop will extract the label from 1st index of on array
            for (var index in arrData[0]) {
                
                //Now convert each value to string and comma-seprated
                row += index + ","
            }
            
            row = row.slice(0, -1)
            
            //append Label row with line break
            CSV += row + "\r\n"
        }
        
        //1st loop is to extract each row
        for (var i = 0; i < arrData.length; i++) {
            var row = ""
            
            //2nd loop will extract each column and convert it in string comma-seprated
            for (const value of Object.values(arrData[i])) {
                row += "\"" + value + "\","
            }
            
            row.slice(0, row.length - 1)
            
            //add a line break after each row
            CSV += row + "\r\n"
        }
        
        if (CSV == "") {
            CBDialogViewShower.alert("Invalid data")
            return
        }
        
        //Generate a file name
        var fileName = "Report_"
        //this will remove the blank-spaces from the title and replace it with an underscore
        fileName += ReportTitle.replace(/ /g, "_")
        
        //Initialize file format you want csv or xls
        const uri = "data:text/csv;charset=utf-8," + escape(CSV)
        
        // Now the little tricky part.
        // you can use either>> window.open(uri);
        // but this will not work in some browsers
        // or you will not get the correct file extension
        
        //this trick will generate a temp <a /> tag
        const link = document.createElement("a")
        link.href = uri
        
        //set the visibility hidden so it will not effect on your web-layout
        link.style.visibility = "hidden"
        link.download = fileName + ".csv"
        
        //this part will append the anchor tag and remove it after automatic click
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
    }
    
    loadSubjectData() {
        
        //this.dropdown.data = new EnterCarInquirySubjectDetailsTopRowView("asd").dataForDropdown()
        
    }
    
    override async viewDidAppear() {
        
        
    }
    
    override async viewWillDisappear() {
        
        
    }
    
    override async handleRoute(route: UIRoute) {
        
        super.handleRoute(route)
        const inquiryComponent = route.componentWithName(InternalUsageDataViewController.routeComponentName)
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding
        
        // View bounds
        const bounds = this.view.bounds.rectangleWithInset(padding)
        
        this.titleLabel.frame = bounds.rectangleWithHeight(labelHeight * 2)
        
        var rowFrame = this.titleLabel.frame.rectangleForNextRow(padding)
        rowFrame.distributeViewsEquallyAlongWidth([this.loadButton, this.downloadButton], padding)
        
        
        rowFrame = rowFrame.rectangleForNextRow(padding, labelHeight * 15 * 2)
        this.chartView.frame = rowFrame.rectangleByAddingX(-padding)
        this.chartView.setMaxSizes(rowFrame.height, rowFrame.width)
        
        rowFrame = rowFrame.rectangleForNextRow(padding, labelHeight * 2)
        this.dateView.frame = rowFrame
        
        rowFrame = rowFrame.rectangleForNextRow(padding, labelHeight * 15 * 2)
        this.tableView.frame = rowFrame
        
        
    }
    
    private async retrieveConnectionsForInstanceIdentifiers(identifiers: string[], minDate: Date, maxDate: Date) {
        
        const result = [] as ClientInstanceConnection[]
        
        for (var i = 0; i < identifiers.length; i++) {
            
            const instanceIdentifierKey = identifiers[i]
            
            const { result: connections } = await SocketClient.RetrieveInstanceConnectionsForInstanceIdentifier(
                { minDate: minDate, maxDate: maxDate, instanceIdentifierKey: instanceIdentifierKey }
            )
            
            connections.forEach(connection => result.push(connection))
            
        }
        
        return result
        
    }
    
    private async retrieveConnectionsForUserProfiles(userProfiles: CBUserProfile[], minDate: Date, maxDate: Date) {
        
        const result = [] as ClientInstanceConnection[]
        
        for (var i = 0; i < userProfiles.length; i++) {
            
            const userProfile = userProfiles[i]
            
            const { result: connections } = await SocketClient.RetrieveInstanceConnectionsForUser(
                { minDate: minDate, maxDate: maxDate, userID: userProfile._id }
            )
            
            connections.forEach(connection => result.push(connection))
            
        }
        
        return result
        
    }
    
    
}
